import React, { useState } from "react";
import { useParams, Link } from "react-router-dom";
import { Sparkles, Loader2, Search } from "lucide-react";
import { searchProgramsNL } from "../api/programsAPI";

/**
 * Natural-language program finder (gap #7 in AI_FIRST_PLATFORM_STRATEGY).
 *
 * Public storefront page. Member types something like
 *   "looking for soccer for my 7yo on Tuesday evenings"
 * The backend extracts intent and ranks the tenant's programs against
 * it. Results link out to the existing /:clubid/register flow with the
 * matched program preselected (just opens that page; pre-selection
 * can be refined later).
 */
export default function ProgramFinder() {
  const { clubid } = useParams<{ clubid: string }>();
  const [query, setQuery] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [response, setResponse] = useState<any>(null);

  const onSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    try {
      const data = await searchProgramsNL(query.trim());
      if (!data) {
        setError("Search service is unavailable right now. Try the program list directly.");
        setResponse(null);
      } else {
        setResponse(data);
      }
    } catch (err: any) {
      setError(err?.message || "Search failed");
    } finally {
      setLoading(false);
    }
  };

  const intent = response?.intent;
  const results = response?.results || [];

  const intentSummary = (() => {
    if (!intent) return null;
    const parts: string[] = [];
    if (intent.ages) parts.push(`age ${intent.ages[0]}-${intent.ages[1]}`);
    if (intent.participant_type === "self") parts.push("for you");
    if (intent.participant_type === "athlete") parts.push("for a child");
    if (intent.days?.length) parts.push(`on ${intent.days.join(", ")}`);
    if (intent.time_window) parts.push(intent.time_window);
    if (intent.keywords?.length) parts.push(`keywords: ${intent.keywords.join(", ")}`);
    return parts.length > 0 ? parts.join(" · ") : null;
  })();

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-zinc-900 py-8 px-4">
      <div className="max-w-3xl mx-auto space-y-6">
        <div className="text-center">
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center justify-center gap-2">
            <Sparkles className="w-7 h-7 text-purple-500" />
            Find a Program
          </h1>
          <p className="text-gray-600 dark:text-gray-300 mt-2">
            Tell us what you're looking for in plain language. We'll match it to our programs.
          </p>
        </div>

        <form onSubmit={onSubmit} className="bg-white dark:bg-zinc-800 rounded-lg shadow p-4 flex gap-2">
          <input
            aria-label="Describe what you are looking for"
            type="text"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            placeholder='e.g. "tennis for my 8 year old, weeknights" or "beginner ballet for me"'
            className="flex-1 px-4 py-3 border border-gray-300 dark:border-zinc-600 rounded-md text-gray-900 dark:text-white bg-white dark:bg-zinc-900 placeholder-gray-400 focus:ring-2 focus:ring-purple-500 focus:outline-none"
            maxLength={500}
            autoFocus
          />
          <button
            type="submit"
            disabled={loading || !query.trim()}
            className="inline-flex items-center gap-2 px-5 py-3 bg-purple-600 hover:bg-purple-700 text-white rounded-md font-medium disabled:opacity-50"
          >
            {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Search className="w-4 h-4" />}
            Search
          </button>
        </form>

        {error && (
          <div className="bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-700/40 text-red-700 dark:text-red-300 rounded-md p-3 text-sm">
            {error}
          </div>
        )}

        {intentSummary && (
          <div className="text-sm text-gray-500 dark:text-gray-400">
            Looking for: <span className="font-medium">{intentSummary}</span>
          </div>
        )}

        {response && results.length === 0 && (
          <div className="bg-white dark:bg-zinc-800 rounded-lg shadow p-6 text-center text-gray-600 dark:text-gray-300">
            No matching programs found. Try different words, or{" "}
            <Link to={`/${clubid}/programs`} className="text-purple-600 hover:underline">
              browse all programs
            </Link>
            .
          </div>
        )}

        {results.length > 0 && (
          <div className="space-y-3">
            {results.map((r: any) => (
              <Link
                key={r.id}
                to={`/${clubid}/register?program_id=${r.id}`}
                className="block bg-white dark:bg-zinc-800 rounded-lg shadow p-4 hover:shadow-md hover:border-purple-300 border border-transparent transition-all"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="text-lg font-semibold text-gray-900 dark:text-white">{r.label}</h3>
                    {r.description && (
                      <p className="text-sm text-gray-600 dark:text-gray-300 mt-1 line-clamp-2">{r.description}</p>
                    )}
                    <div className="mt-2 flex flex-wrap gap-1">
                      {r.match_reasons?.map((reason: string, i: number) => (
                        <span key={i} className="text-xs px-2 py-0.5 rounded-full bg-purple-100 dark:bg-purple-900/30 text-purple-700 dark:text-purple-300">
                          {reason}
                        </span>
                      ))}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 dark:text-gray-400 shrink-0">
                    Ages {r.min_age}-{r.max_age}
                  </div>
                </div>
              </Link>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
